import { create } from "zustand";
import { authClient } from "@/lib/auth-client";
import { queryClient } from "@/lib/queryClient";

/** User shape used across the app (matches better-auth user + additionalFields). */
export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  subscriptionPlan?: string;
  role?: string;
}

interface AuthState {
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string, callbackURL?: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string, callbackURL?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isLoading: false,
  error: null,

  login: async (email, password, callbackURL = "/dashboard") => {
    set({ isLoading: true, error: null });
    const { data, error } = await authClient.signIn.email({
      email,
      password,
      callbackURL,
    });
    set({ isLoading: false });
    if (error) {
      set({ error: error.message || "Login failed" });
      return false;
    }
    return !!data;
  },

  register: async (email, password, name, callbackURL = "/dashboard") => {
    set({ isLoading: true, error: null });
    const { data, error } = await authClient.signUp.email({
      email,
      password,
      name: name ?? email.split("@")[0] ?? "User",
      callbackURL,
    });
    set({ isLoading: false });
    if (error) {
      set({ error: error.message || "Registration failed" });
      return false;
    }
    return !!data;
  },

  logout: async () => {
    await authClient.signOut();
    // Clear all cached data so the next user never sees the previous user's data
    queryClient.clear();
  },

  clearError: () => set({ error: null }),
}));
